import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { UserPlus, X, Mail, Phone, Lock, User, Shield, Check, Copy } from "lucide-react";
import Button from "../../../components/common/Button";
import Input from "../../../components/common/Input";
import { AdminUserData } from "../../../services/userService";
import { useProvisionUserMutation } from "../../../hooks/queries/useUserAdminQueries";
import { useToast } from "../../../hooks/otherHooks/useToast";

interface ProvisionUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (newUser: AdminUserData) => void;
}

const ProvisionUserModal: React.FC<ProvisionUserModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { addToast } = useToast();
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    role: "STUDENT",
    password: "",
  });
  const [provisionedCreds, setProvisionedCreds] = useState<{ email: string; password: string } | null>(null);

  // Prevent background page scrolling while modal is open
  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  const { mutateAsync: provisionUserMutation, isPending: isSubmitting } =
    useProvisionUserMutation();

  if (!isOpen) return null;
  if (typeof document === "undefined") return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email.trim()) {
      addToast("Email address is required.", "error");
      return;
    }

    try {
      const res = await provisionUserMutation({
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: formData.email.trim(),
        phone_number: formData.phone_number.trim(),
        role: formData.role,
        roles: [formData.role],
        password: formData.password.trim() || undefined,
      });

      addToast(res.message || "User provisioned successfully!", "success");
      onSuccess(res.user);

      if (res.temporary_password) {
        setProvisionedCreds({
          email: formData.email,
          password: res.temporary_password,
        });
      } else {
        onClose();
      }
    } catch (err: any) {
      console.error("Failed to provision user:", err);
      const errorMsg = err?.response?.data?.detail || "Failed to provision user account.";
      addToast(errorMsg, "error");
    }
  };

  const copyCredentials = () => {
    if (provisionedCreds) {
      navigator.clipboard.writeText(`Email: ${provisionedCreds.email}\nPassword: ${provisionedCreds.password}`);
      addToast("Credentials copied to clipboard!", "success");
    }
  };

  return createPortal(
    <AnimatePresence>
      <div
        className="cht-fixed cht-inset-0 cht-w-screen cht-h-screen cht-z-[99999] cht-flex cht-items-center cht-justify-center cht-p-4 sm:cht-p-6 cht-bg-slate-950/70 cht-backdrop-blur-sm cht-overflow-y-auto"
        role="dialog"
        aria-modal="true"
        onClick={() => {
          setProvisionedCreds(null);
          onClose();
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 15 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          onClick={(e) => e.stopPropagation()}
          className="cht-bg-white cht-rounded-[28px] cht-max-w-md cht-w-full cht-shadow-2xl cht-border cht-border-gray-100 cht-overflow-hidden cht-relative cht-z-10 cht-my-auto"
        >
          {/* Header */}
          <div className="cht-p-6 cht-border-b cht-border-gray-100 cht-flex cht-items-center cht-justify-between">
            <div className="cht-flex cht-items-center cht-gap-3">
              <div className="cht-w-10 cht-h-10 cht-rounded-xl cht-bg-[#3e57da]/10 cht-text-[#3e57da] cht-flex cht-items-center cht-justify-center">
                <UserPlus size={20} />
              </div>
              <div>
                <h3 className="cht-text-lg cht-font-black cht-text-slate-900 !cht-font-heading">
                  Provision User Account
                </h3>
                <p className="cht-text-xs cht-text-slate-500 font-medium">
                  Direct administrative onboarding
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                setProvisionedCreds(null);
                onClose();
              }}
              className="cht-w-8 cht-h-8 cht-rounded-full cht-flex cht-items-center cht-justify-center cht-text-slate-400 hover:cht-bg-slate-100 hover:cht-text-slate-700 cht-transition-colors"
            >
              <X size={18} />
            </button>
          </div>

          {provisionedCreds ? (
            <div className="cht-p-6 cht-space-y-5">
              <div className="cht-w-12 cht-h-12 cht-rounded-2xl cht-bg-emerald-50 cht-text-emerald-600 cht-flex cht-items-center cht-justify-center cht-mx-auto">
                <Check size={24} />
              </div>
              <div className="cht-text-center">
                <h4 className="cht-text-base cht-font-bold cht-text-slate-900">
                  Account Ready & Provisioned
                </h4>
                <p className="cht-text-xs cht-text-slate-500 cht-mt-1">
                  Share these initial login credentials with the user:
                </p>
              </div>

              <div className="cht-p-4 cht-rounded-2xl cht-bg-slate-50 cht-border cht-border-slate-200 cht-space-y-2 cht-font-mono cht-text-xs">
                <div className="cht-flex cht-items-center cht-justify-between">
                  <span className="cht-text-slate-400">Email:</span>
                  <span className="cht-text-slate-900 cht-font-bold">{provisionedCreds.email}</span>
                </div>
                <div className="cht-flex cht-items-center cht-justify-between">
                  <span className="cht-text-slate-400">Temporary Password:</span>
                  <span className="cht-text-[#3e57da] cht-font-bold">{provisionedCreds.password}</span>
                </div>
              </div>

              <div className="cht-flex cht-items-center cht-gap-3 cht-pt-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="cht-flex-1 cht-rounded-xl"
                  leftIcon={<Copy size={14} />}
                  onClick={copyCredentials}
                >
                  Copy Credentials
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  className="cht-flex-1 cht-rounded-xl"
                  onClick={() => {
                    setProvisionedCreds(null);
                    onClose();
                  }}
                >
                  Done
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="cht-p-6 cht-space-y-4">
              <div className="cht-grid cht-grid-cols-2 cht-gap-3">
                <Input
                  label="First Name"
                  placeholder="e.g. John"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  leftIcon={<User size={16} />}
                />
                <Input
                  label="Last Name"
                  placeholder="e.g. Doe"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  leftIcon={<User size={16} />}
                />
              </div>

              <Input
                label="Email Address *"
                type="email"
                required
                placeholder="john.doe@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                leftIcon={<Mail size={16} />}
              />

              <Input
                label="Phone Number"
                type="tel"
                placeholder="+234..."
                value={formData.phone_number}
                onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                leftIcon={<Phone size={16} />}
              />

              <div className="cht-space-y-1.5">
                <label className="cht-text-xs cht-font-bold cht-text-slate-700">
                  Initial Role
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="cht-w-full cht-bg-gray-50 cht-border cht-border-gray-200 cht-rounded-xl cht-py-2.5 cht-px-3.5 cht-text-xs cht-font-bold text-slate-800 focus:cht-ring-2 focus:cht-ring-[#3e57da]/20"
                >
                  <option value="STUDENT">Student (Learner)</option>
                  <option value="INSTRUCTOR">Academic Faculty (Instructor)</option>
                  <option value="ADMIN">System Administrator</option>
                  <option value="SUPERADMIN">SuperAdmin (Root Authority)</option>
                </select>
              </div>

              <Input
                label="Temporary Password (optional)"
                type="text"
                placeholder="Leave blank to auto-generate"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                leftIcon={<Lock size={16} />}
              />

              <div className="cht-flex cht-items-center cht-justify-end cht-gap-3 cht-pt-3 cht-border-t cht-border-gray-100">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="cht-rounded-xl"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  size="sm"
                  className="cht-rounded-xl cht-px-6"
                  isLoading={isSubmitting}
                >
                  Provision User
                </Button>
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body
  );
};

export default ProvisionUserModal;
